/* eslint-disable no-param-reassign */
/* eslint-disable no-unused-vars */
import Vue from 'vue';
import { cloneObject } from '@/helpers/utility';
import { calculateStationTimesHelper, formatStationStatsData } from '../../components/routeList/utils';
import routeListService from '../../services/routeList';
import {
  normalizePersonnelData,
  getStationBaseData,
} from '../../components/routeList/editRoute/v2/utils/personnelHelpers';

export default {
  state: {
    stationList: { TO: [], FROM: [] },
    tempIdsOnThisRoute: { TO: new Set(), FROM: new Set() },
    isStationsUpdated: { TO: false, FROM: false },
    isCalculatingStationTimes: false,
    stationPanel: {
      showPanel: false,
      stationData: null,
      isEdit: false,
    },
    isStationsLoading: false,
    isInfoWindowOpen: false,
    // Pending reverse-direction changes, tracked per direction: set when a stop is
    // moved or reordered on a round-trip route. The "apply to reverse direction?"
    // dialog is deferred to the Calculate button click (see routeActions.vue) instead
    // of asking on every action. Per-direction tracking ensures we only prompt when
    // the user actually made changes on the direction they're calculating.
    pendingReverseReorder: { TO: false, FROM: false },
    pendingReverseLocationStationIds: { TO: [], FROM: [] },
  },

  mutations: {
    SET_STATION_LIST(state, { route, payload }) {
      Vue.set(state.stationList, route, payload);
    },

    RESET_STATION_LIST(state) {
      Vue.set(state, 'stationList', { TO: [], FROM: [] });
      // Clear pending reverse state so stale flags/IDs don't carry over to a newly loaded route.
      Vue.set(state, 'pendingReverseReorder', { TO: false, FROM: false });
      Vue.set(state, 'pendingReverseLocationStationIds', { TO: [], FROM: [] });
    },

    ADD_STATION_BY_DIRECTION(state, { route, payload }) {
      const clonedPayload = cloneObject(payload);
      Vue.set(state.stationList[route], state.stationList[route].length, clonedPayload);
    },

    ADD_STATION_TO(state, payload) {
      const clonedPayload = cloneObject(payload);
      Vue.set(state.stationList.TO, state.stationList.TO.length, clonedPayload);
    },

    ADD_STATION_FROM(state, payload) {
      const clonedPayload = cloneObject(payload);
      Vue.set(state.stationList.FROM, state.stationList.FROM.length, clonedPayload);
    },

    ADD_STATION_CURRENT_DIRECTION(state, { payload, routeDirection }) {
      const clonedPayload = cloneObject(payload);

      // Ensure the route direction exists and is initialized
      if (!state.stationList[routeDirection]) {
        Vue.set(state.stationList, routeDirection, []);
      }

      Vue.set(state.stationList[routeDirection], state.stationList[routeDirection].length, clonedPayload);
    },

    ADD_STATION_UNSHIFT(state, { route, payload }) {
      const clonedPayload = cloneObject(payload);
      state.stationList[route].unshift(clonedPayload);

      const { id, counterStopId } = payload;
      const routeReverse = route === 'TO' ? 'FROM' : 'TO';
      if (counterStopId) {
        const indexReverse = state.stationList[routeReverse].findIndex((item) => item.id === counterStopId);
        if (indexReverse !== -1) {
          Vue.set(state.stationList[routeReverse][indexReverse], 'counterStopId', id);
        }
      }
    },

    UPDATE_STATION_ID(state, { route, oldId, newId }) {
      if (!state.stationList[route]) return;
      const index = state.stationList[route].findIndex((el) => el.id === oldId);
      if (index !== -1) {
        Vue.set(state.stationList[route][index], 'id', newId);
      }
      // Remap pending reverse location IDs so they track the station's current id.
      // Without this, editing the same stop twice (which reassigns its id via addStation)
      // would leave a stale id in the pending array, and Calculate couldn't find the station.
      const pendingIds = state.pendingReverseLocationStationIds[route] || [];
      const pendingIndex = pendingIds.indexOf(oldId);
      if (pendingIndex !== -1) {
        const updated = [...pendingIds];
        updated[pendingIndex] = newId;
        Vue.set(state.pendingReverseLocationStationIds, route, updated);
      }
    },

    UPDATE_STATION_COUNTER_STOP_ID(state, { route, stationIndex, counterStopId }) {
      if (!state.stationList[route] || stationIndex === -1) return;
      Vue.set(state.stationList[route][stationIndex], 'counterStopId', counterStopId);
    },

    UPDATE_STATION_DATA(state, { route, stationId, key, value }) {
      if (!state.stationList[route]) return;
      const index = state.stationList[route].findIndex((el) => el.id === stationId);
      if (index !== -1) {
        Vue.set(state.stationList[route][index], key, value);
      }
    },

    UPDATE_STATION_DESCRIPTION(state, { route, stationId, description }) {
      if (!state.stationList[route]) return;
      const index = state.stationList[route].findIndex((el) => el.id === stationId);
      if (index !== -1) {
        Vue.set(state.stationList[route][index], 'description', description);
      }
    },

    UPDATE_STATION_LOCATION(state, { route, stationId, location }) {
      if (!state.stationList[route]) return;
      const index = state.stationList[route].findIndex((el) => el.id === stationId);
      if (index !== -1) {
        Vue.set(state.stationList[route][index], 'location', location);
      }
    },

    UPDATE_STATION_ADDRESS(state, { route, stationId, address }) {
      if (!state.stationList[route]) return;
      const index = state.stationList[route].findIndex((el) => el.id === stationId);
      if (index !== -1) {
        Vue.set(state.stationList[route][index], 'address', address);
      }
    },

    REMOVE_STATION_BY_ID(state, { route, stationId }) {
      if (!state.stationList[route]) return;
      const index = state.stationList[route].findIndex((el) => el.id === stationId);
      if (index !== -1) {
        state.stationList[route].splice(index, 1);
      }
    },

    UPDATE_STATION_TIMES(state, { route, stations }) {
      stations.forEach((stationData) => {
        const { index, expectedArrivalHour, expectedDepartureHour, distance, duration } = stationData;
        if (state.stationList[route][index]) {
          Vue.set(state.stationList[route][index], 'expectedArrivalHour', expectedArrivalHour);
          Vue.set(state.stationList[route][index], 'expectedDepartureHour', expectedDepartureHour);
          Vue.set(state.stationList[route][index], 'distance', distance);
          Vue.set(state.stationList[route][index], 'duration', duration);
        }
      });
    },

    SET_STATIONS_UPDATED(state, { route, value }) {
      Vue.set(state.isStationsUpdated, route, value);
    },

    SET_PENDING_REVERSE_REORDER(state, { route, value }) {
      Vue.set(state.pendingReverseReorder, route, value);
    },

    ADD_PENDING_REVERSE_LOCATION_STATION(state, { route, stationId }) {
      if (!stationId || !route) return;
      const current = state.pendingReverseLocationStationIds[route] || [];
      if (!current.includes(stationId)) {
        Vue.set(state.pendingReverseLocationStationIds, route, [...current, stationId]);
      }
    },

    REMOVE_PENDING_REVERSE_LOCATION_STATION(state, { route, stationId }) {
      if (!stationId || !route) return;
      const current = state.pendingReverseLocationStationIds[route] || [];
      if (current.includes(stationId)) {
        Vue.set(
          state.pendingReverseLocationStationIds,
          route,
          current.filter((id) => id !== stationId),
        );
      }
    },

    CLEAR_PENDING_REVERSE_CHANGES(state, route = null) {
      if (route) {
        Vue.set(state.pendingReverseReorder, route, false);
        Vue.set(state.pendingReverseLocationStationIds, route, []);
      } else {
        Vue.set(state, 'pendingReverseReorder', { TO: false, FROM: false });
        Vue.set(state, 'pendingReverseLocationStationIds', { TO: [], FROM: [] });
      }
    },

    ASSIGN_PERSONNEL_TO_STATION(state, { route, personnelId, personnelToAdd, stationId }) {
      // Initialize stationList[route] if it doesn't exist
      if (!state.stationList[route]) {
        Vue.set(state.stationList, route, []);
      }

      // Find and remove personnel from their original station in stationList
      state.stationList[route].forEach((station, index) => {
        if (station.personnelIds && station.personnelIds.includes(personnelId)) {
          const filteredPersonnelIds = station.personnelIds.filter((id) => id !== personnelId);
          Vue.set(state.stationList[route][index], 'personnelIds', filteredPersonnelIds);
        }
        if (station.personnels && station.personnels.some((p) => p.id === personnelId)) {
          const filteredPersonnels = station.personnels.filter((p) => p.id !== personnelId);
          Vue.set(state.stationList[route][index], 'personnels', filteredPersonnels);
        }
      });

      // Add personnel to the new station
      const stationIndex = state.stationList[route].findIndex((el) => el.id === stationId);

      if (stationIndex !== -1) {
        const personnelIds = state.stationList[route][stationIndex].personnelIds || [];
        const personnels = state.stationList[route][stationIndex].personnels || [];

        Vue.set(state.stationList[route][stationIndex], 'personnelIds', [...personnelIds, personnelId]);
        Vue.set(state.stationList[route][stationIndex], 'personnels', [
          ...personnels,
          JSON.parse(JSON.stringify(personnelToAdd.personnel)),
        ]);
      }
    },

    REMOVE_PERSONNEL_FROM_STATION(state, { route, personnelId, stationId }) {
      const stationIndex = state.stationList[route].findIndex((el) => el.id === stationId);
      if (stationIndex === -1) return;

      const station = state.stationList[route][stationIndex];

      // Remove from station arrays
      if (station.personnelIds) {
        const personnelIndex = station.personnelIds.indexOf(personnelId);
        if (personnelIndex !== -1) {
          station.personnelIds.splice(personnelIndex, 1);
        }
      }

      if (station.personnels) {
        const personnelObjIndex = station.personnels.findIndex((p) => p.id === personnelId);
        if (personnelObjIndex !== -1) {
          station.personnels.splice(personnelObjIndex, 1);
        }
      }
    },

    UPDATE_PERSONNEL_STATION_ID(state, { route, oldStationId, newStationId }) {
      const oldStationIndex = state.stationList[route].findIndex((el) => el.id === oldStationId);
      const newStationIndex = state.stationList[route].findIndex((el) => el.id === newStationId);

      if (oldStationIndex !== -1 && newStationIndex !== -1) {
        const oldStation = state.stationList[route][oldStationIndex];
        const newStation = state.stationList[route][newStationIndex];

        // Move personnel from old station to new station
        if (oldStation.personnelIds && oldStation.personnelIds.length > 0) {
          if (!newStation.personnelIds) {
            Vue.set(newStation, 'personnelIds', []);
          }
          if (!newStation.personnels) {
            Vue.set(newStation, 'personnels', []);
          }

          newStation.personnelIds.push(...oldStation.personnelIds);
          newStation.personnels.push(...(oldStation.personnels || []));

          // Clear old station personnel
          Vue.set(oldStation, 'personnelIds', []);
          Vue.set(oldStation, 'personnels', []);
        }
      }
    },

    ADD_TEMP_IDS_ON_THIS_ROUTE(state, { route, payload }) {
      state.tempIdsOnThisRoute[route].add(payload);
    },

    SET_CALCULATING_STATION_TIMES(state, isCalculating) {
      state.isCalculatingStationTimes = isCalculating;
    },

    // Station Panel Mutations
    SET_STATION_PANEL_VISIBILITY(state, showPanel) {
      state.stationPanel.showPanel = showPanel;
    },

    SET_STATION_PANEL_DATA(state, stationData) {
      state.stationPanel.stationData = stationData;
    },

    SET_STATION_PANEL_EDIT_MODE(state, isEdit) {
      state.stationPanel.isEdit = isEdit;
    },

    RESET_STATION_PANEL(state) {
      state.stationPanel = {
        showPanel: false,
        stationData: null,
        isEdit: false,
      };
    },

    UPDATE_STATION_PANEL(state, { showPanel, stationData, isEdit }) {
      if (showPanel !== undefined) {
        state.stationPanel.showPanel = showPanel;
      }
      if (stationData !== undefined) {
        state.stationPanel.stationData = stationData;
      }
      if (isEdit !== undefined) {
        state.stationPanel.isEdit = isEdit;
      }
    },
    SET_STATIONS_LOADING(state, isLoading) {
      state.isStationsLoading = isLoading;
    },
    SET_INFO_WINDOW_OPEN(state, isOpen) {
      state.isInfoWindowOpen = isOpen;
    },
  },

  actions: {
    prepareStopData({ commit, state, rootState, rootGetters }) {
      commit('RESET_STATION_LIST');

      // Get current version source from routeEdit module
      const currentSource = rootState.routeEdit?.routeVersionSource || 'draft';

      // Get version data based on current source
      let versionData;
      if (currentSource === 'active') {
        const { activeVersion, activeStations } = rootState.routeEdit?.storedRouteVersion || {};
        versionData = { version: activeVersion, stations: activeStations };
      } else {
        const { draftVersion, draftStations } = rootState.routeEdit?.storedRouteVersion || {};
        versionData = { version: draftVersion, stations: draftStations };
      }

      if (!versionData.version) {
        console.warn(`No stored ${currentSource} version available for station preparation`);
        commit('RESET_STATION_LIST');
        return;
      }

      const { version, stations } = versionData;
      const { routeDetail } = version;

      if (!routeDetail) {
        console.warn(`No route detail available in stored ${currentSource} version`);
        commit('RESET_STATION_LIST');
        return;
      }

      if (!stations || stations.length === 0) {
        console.warn(`No stations available in ${currentSource} version`);
        commit('RESET_STATION_LIST');
        return;
      }

      // CRITICAL: Filter stations to only include those with routeId property
      // This is consistent with routeList.js logic and prevents matching stations without route context
      const validStations = stations.filter((station) => station.routeId);

      if (validStations.length === 0) {
        console.warn(`No stations with routeId found in ${currentSource} version`, {
          totalStations: stations.length,
          stationsWithoutRouteId: stations.filter((s) => !s.routeId).length,
        });
        commit('RESET_STATION_LIST');
        return;
      }

      // Get current route ID from route shifts store to filter stations correctly
      // CRITICAL: Use version.routeId as primary source since it's always correct for the data being processed
      // Only fall back to currentRouteId if version.routeId is not available
      const currentRouteId = version.routeId || rootState.routeShifts?.currentRouteId;

      // Populate TO direction stations
      const routeStopIds = routeDetail.path?.lstRouteStopIds || [];
      routeStopIds.forEach((stationId) => {
        // Filter stations by both stationId AND current routeId
        // Draft service creates multiple instances of same station for different route shifts
        const foundStation = stations.find((station) => station.id === stationId && station.routeId === currentRouteId);
        if (foundStation) {
          // Commit raw station; UI mapping is handled in components
          commit('ADD_STATION_TO', foundStation);
        } else {
          console.warn(
            `Station with ID ${stationId} not found in stations array for TO route (routeId: ${currentRouteId})`,
          );
        }
      });

      // Populate FROM direction stations
      const returnRouteStopIds = routeDetail.returnPath?.lstRouteStopIds || [];
      returnRouteStopIds.forEach((stationId) => {
        const foundStation = stations.find((station) => station.id === stationId && station.routeId === currentRouteId);
        if (foundStation) {
          // Commit raw station; UI mapping is handled in components
          commit('ADD_STATION_FROM', foundStation);
        } else {
          console.warn(
            `Station with ID ${stationId} not found in stations array for FROM route (routeId: ${currentRouteId})`,
          );
        }
      });
    },

    addStationCurrentDirection({ commit, rootState }, payload) {
      const routeDirection = rootState.routeEdit?.isRouteToggleOn ? 'TO' : 'FROM';
      commit('ADD_STATION_CURRENT_DIRECTION', { payload, routeDirection });
    },
    addStationReverseDirection({ commit, rootState }, payload) {
      const route = rootState.routeEdit?.isRouteToggleOn ? 'FROM' : 'TO';
      commit('ADD_STATION_UNSHIFT', { payload, route });
    },

    async calculateStationTime(
      { commit, state },
      { route, sections, serviceTimeForStopInSeconds, isInbound = true, campusCount = 0 },
    ) {
      try {
        commit('SET_CALCULATING_STATION_TIMES', true);

        const stationsArray = state.stationList[route];
        if (!stationsArray || stationsArray.length === 0) {
          commit('SET_CALCULATING_STATION_TIMES', false);
          return { error: true, message: 'No stations found for calculation' };
        }

        const calculatedStations = calculateStationTimesHelper(
          stationsArray,
          sections,
          serviceTimeForStopInSeconds,
          isInbound,
          campusCount,
        );

        commit('UPDATE_STATION_TIMES', { route, stations: calculatedStations });
        commit('SET_STATIONS_UPDATED', { route, value: true });
        commit('SET_CALCULATING_STATION_TIMES', false);

        return { error: false, message: 'Station time calculation completed successfully' };
      } catch (error) {
        commit('SET_CALCULATING_STATION_TIMES', false);
        console.error('Error in calculateStationTime action:', error);
        return { error: true, message: 'Station time calculation failed' };
      }
    },

    async removePersonnelFromStation({ commit }, { route, personnelId, stationId }) {
      try {
        // Remove from station arrays
        commit('REMOVE_PERSONNEL_FROM_STATION', { route, personnelId, stationId });

        // Remove from personnel-station mapping in routeEdit
        commit('REMOVE_PERSONNEL_STATION_MAPPING', { route, personnelId });

        // Update instancePersonnel array to clear station assignment
        commit('routePersonnel/UPDATE_PERSONNEL_STATION_ASSIGNMENT', { personnelId }, { root: true });

        return { success: true };
      } catch (error) {
        console.error('removePersonnelFromStation action error:', error);
        return { success: false, message: 'Failed to remove personnel from station' };
      }
    },

    async removeStation(
      { commit, state, getters, rootState },
      { stationId, reverseDirection = false, removeCounterStation = false } = {},
    ) {
      try {
        let route = rootState.routeEdit?.isRouteToggleOn ? 'TO' : 'FROM';
        if (reverseDirection) route = route === 'TO' ? 'FROM' : 'TO';

        if (!state.stationList[route]) {
          return { success: false, message: 'Invalid route specified' };
        }
        const counterStationId = getters.counterStationId({ stationId, reverseDirection });
        const ids = getters.listOfPersonnelIds({ route, stationId });

        ids.forEach((id) => {
          commit('ADD_TEMP_IDS_ON_THIS_ROUTE', { route, payload: id });
        });

        commit('REMOVE_STATION_BY_ID', { route, stationId });
        commit('SET_STATIONS_UPDATED', { route, value: true });
        // Removing a stop changes the direction's geometry/times: invalidate so the Save
        // guard blocks until it is recalculated (the editor handler auto-recalculates it).
        commit('SET_IS_CALCULATED', { route, value: false });

        // Optionally remove counter station on reverse route
        if (removeCounterStation && counterStationId) {
          const reverseRoute = route === 'TO' ? 'FROM' : 'TO';
          const reverseIds = getters.listOfPersonnelIds({ route: reverseRoute, stationId: counterStationId });
          reverseIds.forEach((id) => {
            commit('ADD_TEMP_IDS_ON_THIS_ROUTE', { route: reverseRoute, payload: id });
          });
          commit('REMOVE_STATION_BY_ID', { route: reverseRoute, stationId: counterStationId });
          commit('SET_STATIONS_UPDATED', { route: reverseRoute, value: true });
          commit('SET_IS_CALCULATED', { route: reverseRoute, value: false });
        }

        commit('routeShifts/SET_SHOULD_SYNC_STATIONS', true, { root: true });

        return { success: true, message: 'Station removed successfully' };
      } catch (error) {
        console.error('Error removing station:', error);
        return { success: false, message: 'Failed to remove station' };
      }
    },

    async createStation({ commit, dispatch, rootState }, { payload, reverseDirection = false, isPassthrough = null }) {
      try {
        commit('SET_STATIONS_LOADING', true);
        const addStationResponse = await routeListService.addStation(payload);
        // Always explicitly set isPassthrough to ensure it's defined (true or false)
        addStationResponse.isPassthrough = isPassthrough === true;
        if (reverseDirection) {
          dispatch('addStationReverseDirection', addStationResponse);
        } else {
          dispatch('addStationCurrentDirection', addStationResponse);
        }
        commit('ADD_MODIFIED_ROUTE', payload.routeId);

        // VSER-13692: Invalidate the affected direction so Save is blocked until user
        // presses Calculate. Without this, route could be saved with stale polyline/times.
        const currentDir = rootState.routeEdit?.isRouteToggleOn ? 'TO' : 'FROM';
        const reverseDir = currentDir === 'TO' ? 'FROM' : 'TO';
        const affectedRoute = reverseDirection ? reverseDir : currentDir;
        commit('SET_IS_CALCULATED', { route: affectedRoute, value: false });
        commit('SET_STATIONS_UPDATED', { route: affectedRoute, value: true });

        // Set sync flag to propagate station deletion to other route shifts
        commit('routeShifts/SET_SHOULD_SYNC_STATIONS', true, { root: true });

        return { success: true, response: addStationResponse || null };
      } catch (err) {
        console.error(err);
        return { success: false, response: err };
      } finally {
        commit('SET_STATIONS_LOADING', false);
      }
    },

    async cloneStationsToReverseDirection({ getters, dispatch, commit }) {
      const stations = getters.listOfStationsCurrentTab;
      if (!stations || !stations.length) return { success: true };

      // Clone in reverse order to match reverse direction
      /* eslint-disable no-await-in-loop */
      const reversed = cloneObject(stations);
      for (let i = 0; i < reversed.length; i += 1) {
        const s = reversed[i];
        const { description, routeId, location, id, isPassthrough } = s || {};
        const { latitude, longitude, address } = location || {};

        const payload = {
          address,
          description,
          routeId,
          location: {
            latitude,
            longitude,
            address,
          },
          counterStopId: id,
        };

        try {
          commit('SET_STATIONS_LOADING', true);

          await dispatch('createStation', {
            payload,
            reverseDirection: true,
            isPassthrough,
          });
        } catch (err) {
          // Continue processing remaining stations even if one fails
          // eslint-disable-next-line no-console
          console.error('Failed to create reverse station', err);
        } finally {
          commit('SET_STATIONS_LOADING', false);
        }
      }
      /* eslint-enable no-await-in-loop */
      dispatch('setDisplayFirstCalculationMessageReverseDirection', true, { root: true });
      return { success: true };
    },

    async updateStation(
      { commit, state, rootState, dispatch, getters },
      {
        payload,
        statsData = null,
        animation = null,
        route: routeOverride = null,
        applyToReverse = false,
        previousLocation = null,
      } = {},
    ) {
      if (!payload || !payload.id) {
        throw new Error('Invalid station data provided for update.');
      }
      const currentRoute = rootState.routeEdit?.isRouteToggleOn ? 'TO' : 'FROM';
      const route = routeOverride || currentRoute;
      const { description, location, address, id: oldStationId } = payload;
      const routeId = payload?.routeId || rootState.routeEdit?.storedRouteVersion?.draftVersion?.routeId;

      // Validate that the station exists
      const originalStation = state.stationList[route].find((el) => el.id === oldStationId);
      if (!originalStation) {
        throw new Error('Station not found. Cannot update station.');
      }
      // Snapshot pre-change values: the mutations below (UPDATE_STATION_LOCATION, etc.)
      // update this same station object in place, so reading from `originalStation`
      // any later would reflect the NEW values, not the ones being compared against.
      // Some callers (e.g. marker drag) also mutate the live station object's `.location`
      // directly for optimistic UI feedback before dispatching, which would make
      // `originalStation.location` already reflect the new coordinates by the time we get
      // here — those callers must pass their own pre-change `previousLocation` instead.
      const originalDescription = originalStation.description;
      const preMutationLocation = previousLocation || originalStation.location;
      const originalLocation = preMutationLocation ? { ...preMutationLocation } : null;
      // `effectiveAddress` below is sourced from the backend response's *nested*
      // `location.address` (see effectiveAddress/responseLocation), not the station's
      // top-level `address` field — which often isn't populated yet (it's only set once
      // UPDATE_STATION_ADDRESS has run for this station at least once). Comparing against
      // the top-level field alone made this look "changed" on virtually every first edit.
      const originalAddress = preMutationLocation?.address ?? originalStation.address;

      let addStationResponse = null;
      try {
        commit('SET_STATIONS_LOADING', true);
        addStationResponse = await routeListService.addStation(payload);
      } catch (err) {
        console.error(err);
        return { success: false, error: err };
      } finally {
        commit('SET_STATIONS_LOADING', false);
      }

      const newId = addStationResponse?.id || oldStationId;

      // If statsData is provided, update station times
      if (statsData) {
        dispatch('updateStationArrivalDeparture', {
          ...statsData,
          stationId: newId,
        });
      }

      // If ID changed, update station ID first, then update all personnel references
      if (newId && newId !== oldStationId) {
        // First update the station ID in the list (personnel arrays remain intact on the station)
        commit('UPDATE_STATION_ID', {
          route,
          oldId: oldStationId,
          newId,
        });

        const updatedStation = state.stationList[route].find((s) => s.id === newId);
        // Build from the incoming payload's latest values (the station field commits below
        // run after this block, so reading updatedStation here would use stale description/location).
        const stationBaseData = {
          id: newId,
          name: updatedStation?.name,
          description: description || updatedStation?.description,
          location: location || updatedStation?.location,
        };

        // Collect every personnel that must be re-pointed from oldStationId to newId.
        // We combine two sources to stay robust even when one is out of sync:
        //  1. routesData[route].personnelStations - this is the map persisted as
        //     `pathPersonnelStations` on save. The backend (cleanRoutePathPersonnelList)
        //     DROPS any personnel whose mapped stationId is not present in lstRouteStopIds,
        //     so this mapping MUST be remapped to newId or the rider is silently removed.
        //  2. updatedStation.personnelIds - the personnel currently rendered on the station.
        const personnelStations = rootState.routeEdit?.routesData?.[route]?.personnelStations || {};
        const affectedPersonnelIds = new Set();
        // Normalize ids to Number so the same personnel from both sources dedupes correctly
        const normalizeId = (id) => (Number.isNaN(Number(id)) ? id : Number(id));
        Object.keys(personnelStations).forEach((personnelId) => {
          if (String(personnelStations[personnelId]) === String(oldStationId)) {
            affectedPersonnelIds.add(normalizeId(personnelId));
          }
        });
        (updatedStation?.personnelIds || []).forEach((personnelId) => {
          affectedPersonnelIds.add(normalizeId(personnelId));
        });

        affectedPersonnelIds.forEach((personnelId) => {
          // Update routesData personnelStations mapping (persisted as pathPersonnelStations on save/activate).
          commit('UPDATE_PERSONNEL_STATION_MAPPING', {
            route,
            personnelId,
            stationId: newId,
          });

          // Update instancePersonnel station reference so personnel remains visible on the route
          commit(
            'routePersonnel/UPDATE_PERSONNEL_STATION_ASSIGNMENT',
            {
              personnelId,
              station: stationBaseData,
              direction: route,
            },
            { root: true },
          );
        });

        // Update counterStopId references in reverse direction stations.
        // Only the counterStopId pointer needs updating here: the counter station's own id
        // does not change, so personnel referencing it via their reverse-direction key remain valid.
        if (updatedStation?.counterStopId) {
          const reverseRoute = route === 'TO' ? 'FROM' : 'TO';
          let counterStationIndex = state.stationList[reverseRoute].findIndex(
            (station) => station.id === updatedStation.counterStopId,
          );

          if (counterStationIndex === -1) {
            // Primary lookup failed - this station's OWN counterStopId pointer is stale
            // (points to an id that no longer exists on the reverse direction). Self-heal by
            // falling back to a reverse lookup: does any station on the reverse direction
            // still point BACK to this station's pre-move id? If the reverse side's pointer
            // is intact (even though ours isn't), that identifies the real counter.
            counterStationIndex = state.stationList[reverseRoute].findIndex(
              (station) => station.counterStopId === oldStationId,
            );
          }

          if (counterStationIndex !== -1) {
            commit('UPDATE_STATION_COUNTER_STOP_ID', {
              route: reverseRoute,
              stationIndex: counterStationIndex,
              counterStopId: newId,
            });

            // Self-heal this station's OWN counterStopId field too, so it stays correct for
            // future moves instead of relying on the fallback lookup every time.
            const healedCounterId = state.stationList[reverseRoute][counterStationIndex].id;
            if (healedCounterId !== updatedStation.counterStopId) {
              const movedStationIndex = state.stationList[route].findIndex((s) => s.id === newId);
              if (movedStationIndex !== -1) {
                commit('UPDATE_STATION_COUNTER_STOP_ID', {
                  route,
                  stationIndex: movedStationIndex,
                  counterStopId: healedCounterId,
                });
              }
            }
          }
        }
      }

      if (description) {
        commit('UPDATE_STATION_DESCRIPTION', { route, stationId: newId, description });
      }

      // Prefer the backend response's location/address over the payload's. The backend
      // resolves the address from coordinates (and recalculates geohash), so the response
      // is always more accurate than what we sent — especially after a marker drag where
      // the payload still carries the pre-move address.
      const responseLocation = addStationResponse?.location;
      const effectiveLocation = responseLocation || location;
      const effectiveAddress = responseLocation?.address || address;

      if (effectiveLocation) {
        commit('UPDATE_STATION_LOCATION', { route, stationId: newId, location: effectiveLocation });
      }

      if (effectiveAddress) {
        commit('UPDATE_STATION_ADDRESS', { route, stationId: newId, address: effectiveAddress });
      }

      if (animation !== null && !routeOverride) {
        dispatch('updateStationData', {
          stationId: newId,
          key: 'animation',
          value: animation,
        });
      }

      if (routeId) {
        commit('ADD_MODIFIED_ROUTE', routeId);
      }

      // FR-3: invalidating the affected direction so Save is blocked until the user
      // presses Calculate (consistent with createStation / removeStation flows).
      // Exception: a pure arrival/departure time edit (the panel always resends the
      // station's existing location/address alongside statsData) doesn't affect the
      // route's polyline or stop order, so it must not force a recalculation.
      // Note: `newId` is deliberately NOT compared to `oldStationId` here — addStation is a
      // backend upsert that reassigns the station's id on essentially every call (see the
      // UPDATE_STATION_ID remapping elsewhere in this file), so an id change by itself does
      // not indicate a real structural change and would falsely trigger this on every save.
      const descriptionChanged = Boolean(description) && description !== originalDescription;
      const locationChanged =
        Boolean(effectiveLocation) &&
        (effectiveLocation.latitude !== originalLocation?.latitude ||
          effectiveLocation.longitude !== originalLocation?.longitude);
      const addressChanged = Boolean(effectiveAddress) && effectiveAddress !== originalAddress;
      const hasNonTimeChanges = descriptionChanged || locationChanged || addressChanged;

      if (hasNonTimeChanges) {
        commit('SET_IS_CALCULATED', { route, value: false });
      }
      commit('SET_STATIONS_UPDATED', { route, value: true });

      // FR-3: when the user confirmed "update in both directions", mirror the location
      // change to the counter station on the reverse direction as well. The recursive
      // dispatch uses routeOverride so it targets the reverse route regardless of the
      // current isRouteToggleOn flag.
      let counterStationMissing = false;
      if (applyToReverse) {
        const reverseRoute = route === 'TO' ? 'FROM' : 'TO';
        const counterStationId = getters.counterStationId({ stationId: newId });
        if (counterStationId) {
          const counterStation = state.stationList[reverseRoute].find((s) => s.id === counterStationId);
          if (counterStation) {
            const counterPayload = {
              ...counterStation,
              id: counterStationId,
              location: location || counterStation.location,
              address: address || counterStation.address,
              routeId,
            };
            const reverseResult = await dispatch('updateStation', {
              payload: counterPayload,
              route: reverseRoute,
              applyToReverse: false,
            });
            if (reverseResult?.success === false) {
              return {
                success: false,
                id: newId,
                counterStationMissing: false,
                message: 'Reverse direction update failed',
              };
            }
          }
        } else {
          // FR-3 acceptance: if no counter station is found, inform the caller so the
          // component can notify the user that only the current direction was updated.
          counterStationMissing = true;
        }
      }

      commit('routeShifts/SET_SHOULD_SYNC_STATIONS', true, { root: true });

      return { success: true, id: newId, counterStationMissing };
    },

    async updateStationArrivalDeparture({ commit, state }, statsData) {
      const { templateRouteDirection, stationId, expectedArrivalHour, expectedDepartureHour } = statsData || {};

      const stationStatsPayload = formatStationStatsData(statsData);
      try {
        await routeListService.updateStationStats(stationStatsPayload);
      } catch (error) {
        console.error(error);
        return;
      }

      const route = templateRouteDirection === 'FIRST_LEG' ? 'TO' : 'FROM';
      if (!state.stationList[route]) return;
      const index = state.stationList[route].findIndex((el) => el.id === stationId);
      if (index === -1) return;

      if (expectedArrivalHour !== undefined) {
        commit('UPDATE_STATION_DATA', {
          route,
          stationId,
          key: 'expectedArrivalHour',
          value: expectedArrivalHour,
        });
      }
      if (expectedDepartureHour !== undefined) {
        commit('UPDATE_STATION_DATA', {
          route,
          stationId,
          key: 'expectedDepartureHour',
          value: expectedDepartureHour,
        });
      }
    },

    assignPersonnelToStation({ commit, rootGetters }, { station, assignReverseDirection = false }) {
      const route = rootGetters.toOrFrom;
      const stationId = station.id;
      const personnelId = rootGetters['routePersonnel/personnelToBeAssigned']?.id;
      const rawPersonnelData = rootGetters['routePersonnel/getPersonnelById'](personnelId);

      if (!rawPersonnelData) {
        console.error(`Personnel with ID ${personnelId} not found`);
        return;
      }

      const personnelToAdd = normalizePersonnelData(rawPersonnelData);

      if (!personnelToAdd) {
        console.error(`Failed to normalize personnel data for ID ${personnelId}`);
        return;
      }

      const { routeId } = rootGetters;

      // Update station list
      commit('ASSIGN_PERSONNEL_TO_STATION', { route, personnelId, stationId, personnelToAdd });

      // Update routes data mapping (routeEdit module is non-namespaced, no root needed)
      commit('UPDATE_PERSONNEL_STATION_MAPPING', { route, personnelId, stationId });

      // Update instancePersonnel station assignment
      commit(
        'routePersonnel/UPDATE_PERSONNEL_STATION_ASSIGNMENT',
        { personnelId, station: getStationBaseData(station), routeId, direction: route },
        { root: true },
      );

      if (assignReverseDirection) {
        const reverseRoute = route === 'TO' ? 'FROM' : 'TO';
        const reverseStationId = station.counterStopId;

        // Update station list for reverse direction
        commit('ASSIGN_PERSONNEL_TO_STATION', {
          route: reverseRoute,
          personnelId,
          stationId: reverseStationId,
          personnelToAdd,
        });

        // Update routes data mapping for reverse direction (routeEdit module is non-namespaced, no root needed)
        commit('UPDATE_PERSONNEL_STATION_MAPPING', {
          route: reverseRoute,
          personnelId,
          stationId: reverseStationId,
        });

        const reverseStation = rootGetters.getStationById({ id: reverseStationId });
        // Update instancePersonnel station assignment for reverse direction
        commit(
          'routePersonnel/UPDATE_PERSONNEL_STATION_ASSIGNMENT',
          { personnelId, station: reverseStation, routeId, direction: reverseRoute },
          { root: true },
        );
      }
    },

    resetStationList({ commit }) {
      commit('RESET_STATION_LIST');
    },

    setStationList({ commit }, { route, payload }) {
      commit('SET_STATION_LIST', { route, payload });
    },

    /**
     * Reorder stations for the current direction only. The reverse-direction mirror
     * is deferred to the Calculate button click (see applyPendingChangesToReverseDirection).
     *
     * The current direction is invalidated (SET_IS_CALCULATED = false) so Save is blocked
     * until the user presses Calculate.
     */
    async reorderStations({ commit, state, rootState }, { orderedStations } = {}) {
      const routeCurrent = rootState.routeEdit?.isRouteToggleOn ? 'TO' : 'FROM';

      commit('SET_STATION_LIST', { route: routeCurrent, payload: orderedStations });
      commit('SET_IS_CALCULATED', { route: routeCurrent, value: false });
      commit('SET_STATIONS_UPDATED', { route: routeCurrent, value: true });
      commit('routeShifts/SET_SHOULD_SYNC_STATIONS', true, { root: true });

      return { success: true };
    },

    /**
     * Apply pending reverse-direction changes for BOTH directions in one atomic pass.
     *
     * Snapshots each direction's pending location ids AND reorder flags UP FRONT, before
     * either mirror runs, and passes the OTHER direction's snapshot into
     * applyPendingChangesToReverseDirection for conflict detection. This matters because
     * applyPendingChangesToReverseDirection clears its own direction's pending state when it
     * finishes; if the two directions were processed with a live (post-mutation) read instead
     * of a fixed snapshot, whichever direction is processed SECOND would no longer see that
     * the FIRST direction had its own independent pending change (since it was already
     * cleared), and would incorrectly overwrite it instead of treating it as a conflict to
     * leave alone. The same applies to reorder: the first dispatch overwrites the reverse
     * direction's order before the second dispatch reads it, silently losing one user's edit.
     */
    async applyPendingChangesBothDirections({ state, dispatch }) {
      const snapshot = {
        TO: [...(state.pendingReverseLocationStationIds.TO || [])],
        FROM: [...(state.pendingReverseLocationStationIds.FROM || [])],
      };
      const reorderSnapshot = {
        TO: !!state.pendingReverseReorder.TO,
        FROM: !!state.pendingReverseReorder.FROM,
      };

      const resultTo = await dispatch('applyPendingChangesToReverseDirection', {
        route: 'TO',
        reverseSnapshotIds: snapshot.FROM,
        reverseHasPendingReorder: reorderSnapshot.FROM,
      });
      const resultFrom = await dispatch('applyPendingChangesToReverseDirection', {
        route: 'FROM',
        reverseSnapshotIds: snapshot.TO,
        reverseHasPendingReorder: reorderSnapshot.TO,
      });

      return {
        skippedStationIds: [...(resultTo?.skippedStationIds || []), ...(resultFrom?.skippedStationIds || [])],
        affectedDirections: ['TO', 'FROM'],
      };
    },

    /**
     * Apply pending reverse-direction changes (stop reorder and/or location changes)
     * that were deferred from the interactive editor. Called when the user clicks
     * Calculate and confirms "apply to both directions".
     *
     * Mirrors the current direction's order to the reverse direction (walking backwards
     * and placing counter stations), and mirrors each changed station's location to its
     * counter station via updateStation (API call).
     *
     * `reverseSnapshotIds` and `reverseHasPendingReorder`, when provided, are used instead
     * of live state for conflict detection (see applyPendingChangesBothDirections for why).
     *
     * Returns the list of affected directions so the caller can recalculate them.
     */
    async applyPendingChangesToReverseDirection(
      { commit, state, dispatch, getters, rootState },
      { route: routeOverride = null, reverseSnapshotIds = null, reverseHasPendingReorder = null } = {},
    ) {
      const routeCurrent = routeOverride || (rootState.routeEdit?.isRouteToggleOn ? 'TO' : 'FROM');
      const routeReverse = routeCurrent === 'TO' ? 'FROM' : 'TO';
      const affectedDirections = [routeReverse];
      // Tracks stops that couldn't be mirrored (e.g. stale/missing counterStopId link) so the
      // caller can surface a warning instead of silently doing nothing.
      const skippedStationIds = [];

      // Determine whether the reverse direction ALSO has a pending reorder, using the
      // caller-provided snapshot when available (see applyPendingChangesBothDirections).
      const reverseReorderPending = reverseHasPendingReorder ?? state.pendingReverseReorder[routeReverse] ?? false;

      // Mirror reorder: walk the current-direction order backwards to build the reverse order.
      // Skip if BOTH directions have pending reorder — each side already reflects the user's
      // own edit, and mirroring one onto the other would overwrite the reverse side's order
      // (the same conflict-avoidance strategy used for location changes).
      if (state.pendingReverseReorder[routeCurrent] && !reverseReorderPending) {
        const orderedStations = state.stationList[routeCurrent] || [];
        const reverseStations = state.stationList[routeReverse] || [];
        const usedReverseIds = new Set();
        const reorderedReverse = [];

        for (let i = orderedStations.length - 1; i >= 0; i -= 1) {
          const { id, counterStopId } = orderedStations[i] || {};
          if (counterStopId) {
            let counterStation = reverseStations.find((s) => s.id === counterStopId);
            if (!counterStation) {
              // Primary lookup failed (stale pointer) - fall back to a reverse lookup: does any
              // station on the reverse direction point BACK to this station's id?
              counterStation = reverseStations.find((s) => s.counterStopId === id);
            }
            if (counterStation) {
              reorderedReverse.push(counterStation);
              usedReverseIds.add(counterStation.id);
            } else {
              // counterStopId is set but doesn't resolve to an actual station on the reverse
              // direction, even with the fallback - the order for this stop couldn't be mirrored.
              console.warn(
                `applyPendingChangesToReverseDirection: stale counterStopId for ${routeCurrent} station id=${id} counterStopId=${counterStopId}, not found in ${routeReverse} (no fallback match either)`,
              );
              skippedStationIds.push(id);
            }
          }
        }

        // Preserve reverse-direction stops that have no counter in the current direction.
        reverseStations.forEach((s) => {
          if (!usedReverseIds.has(s.id)) {
            reorderedReverse.push(s);
          }
        });

        commit('SET_STATION_LIST', { route: routeReverse, payload: reorderedReverse });
        commit('SET_IS_CALCULATED', { route: routeReverse, value: false });
        commit('SET_STATIONS_UPDATED', { route: routeReverse, value: true });
      }

      // Mirror location changes: for each changed station, update its counter station's location.
      // Look up the counter station directly from the station's counterStopId field instead of
      // using the counterStationId getter (which depends on the route toggle state and would give
      // wrong results when applying changes for the non-active direction).
      const pendingLocationIds = state.pendingReverseLocationStationIds[routeCurrent] || [];
      // Stations that were ALSO independently moved on the reverse direction. If both sides of a
      // counter pair have their own pending change, mirroring one onto the other would overwrite
      // the reverse side's own edit AND reassign its id (via addStation), which would invalidate
      // the reverse direction's own pending entry when it's processed right after this call
      // (self-invalidating cascade). Since both sides already reflect what the user set directly,
      // skip mirroring for these pairs entirely - there's nothing that needs to be copied over.
      // Use the caller-provided snapshot when available (see applyPendingChangesBothDirections);
      // otherwise fall back to live state (safe for single-direction calls where the reverse
      // direction has no pending changes of its own to lose track of).
      const reversePendingIds = new Set(
        reverseSnapshotIds ?? state.pendingReverseLocationStationIds[routeReverse] ?? [],
      );

      const locationMirrorTasks = pendingLocationIds
        .map((stationId) => {
          const station = state.stationList[routeCurrent].find((s) => s.id === stationId);
          if (!station) {
            skippedStationIds.push(stationId);
            return null;
          }

          const counterId = station.counterStopId;
          if (!counterId) {
            skippedStationIds.push(stationId);
            return null;
          }

          let counterStation = state.stationList[routeReverse].find((s) => s.id === counterId);
          if (!counterStation) {
            // counterStopId is set but doesn't resolve to an actual station on the reverse
            // direction (stale link). Fall back to a reverse lookup: does any station on the
            // reverse direction point BACK to this station's id? Self-heal if found.
            counterStation = state.stationList[routeReverse].find((s) => s.counterStopId === stationId);
          }

          if (!counterStation) {
            console.warn(
              `applyPendingChangesToReverseDirection: counterStopId=${counterId} for station id=${stationId} not found in ${routeReverse} (no fallback match either)`,
            );
            skippedStationIds.push(stationId);
            return null;
          }

          // Conflict check must run AFTER resolving the real counter station (including the
          // stale-link fallback). Checking the raw counterStopId before the fallback would miss
          // conflicts when counterId is stale but the real counter station has a pending move.
          if (reversePendingIds.has(counterStation.id)) {
            // Reverse side was also independently changed - leave both sides as the user set them.
            return null;
          }

          return {
            payload: {
              ...counterStation,
              id: counterStation.id,
              location: station.location,
              address: station.address,
            },
          };
        })
        .filter(Boolean);

      // Sequentially update each counter station (updateStation makes an API call).
      await locationMirrorTasks.reduce(async (previousPromise, task) => {
        await previousPromise;
        try {
          const result = await dispatch('updateStation', {
            payload: task.payload,
            route: routeReverse,
            applyToReverse: false,
          });
          if (result?.success === false) {
            skippedStationIds.push(task.payload.id);
          }
        } catch (err) {
          console.error('applyPendingChangesToReverseDirection: location mirror failed', err);
          skippedStationIds.push(task.payload.id);
        }
        return Promise.resolve();
      }, Promise.resolve());

      commit('CLEAR_PENDING_REVERSE_CHANGES', routeCurrent);
      commit('routeShifts/SET_SHOULD_SYNC_STATIONS', true, { root: true });

      return { affectedDirections, skippedStationIds };
    },

    addStationTo({ commit }, payload) {
      commit('ADD_STATION_TO', payload);
    },

    addStationFrom({ commit }, payload) {
      commit('ADD_STATION_FROM', payload);
    },

    addCounterStation({ commit, state, rootState }, { address, description, routeId, location, id: counterStopId }) {
      if (!counterStopId || !location) {
        console.error('Invalid parameters for addCounterStation');
      }
    },

    updateStationData({ commit, rootState }, { stationId, key, value }) {
      const route = rootState.routeEdit?.isRouteToggleOn ? 'TO' : 'FROM';
      commit('UPDATE_STATION_DATA', { route, stationId, key, value });
    },

    // Station Panel Actions
    showStationPanel({ commit }, { stationData = null, isEdit = false } = {}) {
      commit('UPDATE_STATION_PANEL', {
        showPanel: true,
        stationData,
        isEdit,
      });
    },

    hideStationPanel({ commit }) {
      commit('SET_STATION_PANEL_VISIBILITY', false);
    },

    openStationPanelForEdit({ commit }, stationData) {
      commit('UPDATE_STATION_PANEL', {
        showPanel: true,
        stationData,
        isEdit: true,
      });
    },

    openStationPanelForAdd({ commit }, stationData = null) {
      commit('UPDATE_STATION_PANEL', {
        showPanel: true,
        stationData,
        isEdit: false,
      });
    },

    updateStationPanelData({ commit }, stationData) {
      commit('SET_STATION_PANEL_DATA', stationData);
    },

    resetStationPanel({ commit }) {
      commit('RESET_STATION_PANEL');
    },

    toggleStationPanelEditMode({ commit, state }) {
      commit('SET_STATION_PANEL_EDIT_MODE', !state.stationPanel.isEdit);
    },
  },

  getters: {
    listOfStations: (state) => (route) => state.stationList[route],

    listOfStationsCurrentTab(state, getters, rootState) {
      const route = rootState.routeEdit?.isRouteToggleOn ? 'TO' : 'FROM';
      return state.stationList[route] || [];
    },
    listOfStationsReverseDirection(state, getters, rootState) {
      const route = rootState.routeEdit?.isRouteToggleOn ? 'FROM' : 'TO';
      return state.stationList[route] || [];
    },

    stationsById:
      (state) =>
      ({ route, id }) => {
        if (state.stationList[route].length < 1) return [];
        return state.stationList[route].filter((item) => item.id === id);
      },

    firstStationById:
      (state) =>
      ({ route, id }) =>
        state.stationList[route].find((item) => item.id === id),

    listOfStationsTo(state) {
      return state.stationList.TO;
    },

    listOfStationsFrom(state) {
      return state.stationList.FROM;
    },
    listOfAllStations(state) {
      return [...state.stationList.TO, ...state.stationList.FROM];
    },
    getStationById:
      (state) =>
      ({ id }) =>
        [...state.stationList.TO, ...state.stationList.FROM].find((item) => item.id === id),

    countOfPersonnelById:
      (state) =>
      ({ route, stationId }) => {
        const index = state.stationList[route].findIndex((el) => el.id === stationId);
        if (index < 0) return 0;
        return state.stationList[route][index].personnelIds?.length || 0;
      },

    listOfPersonnelIds:
      (state) =>
      ({ route, stationId }) => {
        const station = state.stationList[route].find((el) => el.id === stationId);
        if (!station) return [];
        return station.personnelIds || [];
      },

    tempIdsOnThisRoute: (state) => (route) => state.tempIdsOnThisRoute[route],

    counterStationId:
      (state, getters) =>
      ({ stationId, reverseDirection = false }) => {
        if (!getters.isRoundTrip) {
          return null;
        }
        const currentWay = getters.toOrFrom;
        const reverseWay = currentWay === 'TO' ? 'FROM' : 'TO';
        const route = reverseDirection ? reverseWay : currentWay;

        const stationCounterId = getters.firstStationById({
          route,
          id: stationId,
        })?.counterStopId;

        let reverseCounterStation = null;
        if (stationCounterId) {
          reverseCounterStation = getters.firstStationById({
            route: reverseWay,
            id: stationCounterId,
          })?.counterStopId;
        }

        // check if the counter station is exists on reverse direction as well
        if (stationCounterId) {
          if (reverseCounterStation === stationId) {
            return stationCounterId;
          }
        }

        return null;
      },

    isStationsLoading(state) {
      return state.isStationsLoading;
    },

    hasPendingReverseChanges:
      (state, getters) =>
      (route = null) => {
        const direction = route || getters.toOrFrom;
        return (
          state.pendingReverseReorder[direction] || (state.pendingReverseLocationStationIds[direction] || []).length > 0
        );
      },
  },
};

/* eslint-enable no-param-reassign */
/* eslint-enable no-unused-vars */
